import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Volume2, Pause, Play, Square, Loader2, AlertTriangle } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, logUsage, errMsg } from "@/lib/api";
import { speak, stopSpeaking, findVoice, VOICE_LANGS } from "@/lib/speech";

function screenFromPath(pathname, search) {
  const p = pathname.split("/").filter(Boolean);
  const q = new URLSearchParams(search);
  if (p[0] === "portal" || p[0] === "admin") return { screen: "admin" };
  if (p[0] === "services" && p[1]) return { screen: "service_detail", service_id: p[1] };
  if (p[0] === "services") return { screen: "catalog", sector_id: q.get("sector") || undefined };
  if (p[0] === "requests" && p[1]) return { screen: "request_detail", ref: p[1] };
  if (p[0] === "requests") return { screen: "requests" };
  if (p[0] === "payments") return { screen: "payments" };
  if (p[0] === "support") return { screen: "support" };
  if (p[0] === "profile") return { screen: "profile" };
  if (p[0] === "architecture") return { screen: "architecture" };
  return { screen: "home" };
}

export const ReadAloudBar = () => {
  const { t, voiceLang, setVoiceLang, me } = useApp();
  const loc = useLocation();
  const [state, setState] = useState("idle");
  const [text, setText] = useState("");
  const [rate, setRate] = useState(1);
  const [noVoice, setNoVoice] = useState(false);

  useEffect(() => { stopSpeaking(); setState("idle"); setText(""); }, [loc.pathname, loc.search]);

  const play = async () => {
    setState("loading");
    try {
      const ctx = screenFromPath(loc.pathname, loc.search);
      const { data } = await api.post("/summary", { ...ctx, lang: voiceLang });
      setText(data.text);
      setNoVoice(!findVoice(voiceLang));
      speak(data.text, voiceLang, rate, { onstart: () => setState("speaking"), onend: () => setState("idle") });
      setState("speaking");
      logUsage({ type: "summary_played", screen: ctx.screen, lang: voiceLang, role: me?.user?.persona || data.role });
    } catch (e) {
      setText(errMsg(e));
      setState("idle");
    }
  };
  const pause = () => { window.speechSynthesis?.pause(); setState("paused"); };
  const resume = () => { window.speechSynthesis?.resume(); setState("speaking"); };
  const stop = () => { stopSpeaking(); setState("idle"); };

  return (
    <div data-testid="read-aloud-bar" className="border-b border-[hsl(var(--accent)/0.3)] bg-[hsl(var(--gold-soft))] px-4 py-2 text-xs">
      <div className="flex items-center gap-2">
        {state === "idle" && (
          <button data-testid="read-aloud-play-btn" onClick={play} aria-label={t("readAloud")}
            className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-primary px-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.03]">
            <Volume2 size={15} /> {t("readAloud")}
          </button>
        )}
        {state === "loading" && <span className="inline-flex items-center gap-1.5 font-semibold"><Loader2 size={15} className="animate-spin" /> {t("thinking")}</span>}
        {state === "speaking" && (
          <button data-testid="read-aloud-pause-btn" onClick={pause} aria-label={t("pause")} className="inline-flex min-h-[36px] items-center gap-1 rounded-full bg-primary px-3 font-semibold text-primary-foreground">
            <Pause size={15} /> {t("pause")}
          </button>
        )}
        {state === "paused" && (
          <button data-testid="read-aloud-resume-btn" onClick={resume} aria-label={t("resume")} className="inline-flex min-h-[36px] items-center gap-1 rounded-full bg-primary px-3 font-semibold text-primary-foreground">
            <Play size={15} /> {t("resume")}
          </button>
        )}
        {(state === "speaking" || state === "paused") && (
          <button data-testid="read-aloud-stop-btn" onClick={stop} aria-label={t("stop")} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card">
            <Square size={13} />
          </button>
        )}
        <div className="ms-auto flex items-center gap-1.5">
          <label className="sr-only" htmlFor="ra-speed">{t("speed")}</label>
          <select id="ra-speed" data-testid="read-aloud-speed-select" value={rate} onChange={(e) => setRate(Number(e.target.value))}
            className="h-9 rounded-lg border border-border bg-card px-1.5">
            {[0.75, 1, 1.25, 1.5].map((r) => <option key={r} value={r}>{`${r}×`}</option>)}
          </select>
          <label className="sr-only" htmlFor="ra-lang">{t("voiceLanguage")}</label>
          <select id="ra-lang" data-testid="read-aloud-lang-select" value={voiceLang} onChange={(e) => setVoiceLang(e.target.value)}
            className="h-9 max-w-[96px] rounded-lg border border-border bg-card px-1.5">
            {VOICE_LANGS.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        </div>
      </div>
      {text && (
        <div data-testid="read-aloud-transcript" className="q1-fade-up mt-2 rounded-lg bg-card/80 p-2.5 leading-relaxed text-foreground" aria-live="polite">
          <span className="font-semibold">{t("transcript")}: </span>{text}
          {noVoice && <p className="mt-1 flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-400"><AlertTriangle size={12} /> {t("noVoice")}</p>}
        </div>
      )}
    </div>
  );
};
